import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * <ScrollRestoration />
 *
 * Drop-in scroll handler for our BrowserRouter (data routers have their own
 * built-in, but we use the classic v6 BrowserRouter so we wire it manually).
 *
 * Behavior:
 *   • PUSH / REPLACE  (clicked a link, used navigate())  → scroll to top
 *   • POP             (browser/mobile back or forward)    → restore previous
 *                                                           scroll position
 *
 * Save strategy: we record (scrollX, scrollY) under
 * `scroll:${pathname}${search}` in sessionStorage whenever the route changes
 * away from a page. On POP back to that page we look up the saved coords and
 * scroll there inside a requestAnimationFrame (so React has finished
 * rendering the page before we move the viewport).
 *
 * No props. Mount it once inside the Router and leave it alone.
 */
export default function ScrollRestoration() {
  const location = useLocation();
  const navType = useNavigationType(); // 'POP' | 'PUSH' | 'REPLACE'

  // Save scroll position whenever we navigate AWAY from the current page.
  useEffect(() => {
    const key = `scroll:${location.pathname}${location.search}`;

    const save = () => {
      try {
        sessionStorage.setItem(
          key,
          JSON.stringify({ x: window.scrollX, y: window.scrollY })
        );
      } catch (_e) {
        /* sessionStorage may be unavailable in private mode — silent ignore */
      }
    };

    // Cover hard exits (browser refresh, tab close, hardware back on mobile
    // that triggers unload). For SPA route changes we save in the cleanup
    // below — that fires when this effect tears down on the next location.
    window.addEventListener('pagehide', save);
    return () => {
      save();
      window.removeEventListener('pagehide', save);
    };
  }, [location.pathname, location.search]);

  // Restore (or scroll-to-top) on every route change.
  useEffect(() => {
    if (navType === 'POP') {
      const key = `scroll:${location.pathname}${location.search}`;
      let saved = null;
      try {
        const raw = sessionStorage.getItem(key);
        if (raw) saved = JSON.parse(raw);
      } catch (_e) {
        saved = null;
      }
      if (saved && typeof saved.y === 'number') {
        const targetY = saved.y;
        const targetX = saved.x || 0;
        // Async-loaded content (product grids, images, blog posts) may not be
        // mounted yet at the time POP fires — the destination page is `0px`
        // tall for a tick. Retry with growing delays until the page is tall
        // enough to actually scroll to the saved Y, or we give up after ~1s.
        // This is the difference between back-button "kinda works" and "feels
        // native."
        const tryRestore = (attempt = 0) => {
          const docH = Math.max(
            document.body.scrollHeight,
            document.documentElement.scrollHeight,
          );
          const viewportH = window.innerHeight;
          // Page is tall enough? Restore.
          if (docH - viewportH >= targetY || attempt >= 8) {
            window.scrollTo(targetX, targetY);
            return;
          }
          // Page still mounting — back off and retry. Delays: 0, 50, 100,
          // 150, 200, 300, 400, 500 ms (≈1.7s ceiling).
          const delay = attempt === 0 ? 0 : 50 + attempt * 50;
          setTimeout(() => tryRestore(attempt + 1), delay);
        };
        requestAnimationFrame(() => tryRestore(0));
        return;
      }
    }
    // Forward navigation (or POP target had no saved position) → top of page.
    window.scrollTo(0, 0);
  }, [location.pathname, location.search, navType]);

  return null;
}
